import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../lib/api';
import { useAuth } from '../context/AuthContext';

// Google Calendar Widget (company-wide connection)
const GoogleCalendarWidget = ({ onSyncComplete }) => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const prevLastSync = useRef(null);
  const isInitialized = useRef(false); // Track first fetch to avoid triggering onSyncComplete on mount

  const canManage = user?.role === 'admin';
  const canSync = ['admin', 'vertriebsleiter', 'backoffice'].includes(user?.role);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/google-calendar/status`, {
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
      });
      setStatus(res.data);
      // Only fire onSyncComplete for REAL changes after initialization (not on first load)
      if (res.data.last_sync) {
        if (isInitialized.current && res.data.last_sync !== prevLastSync.current) {
          if (onSyncComplete) onSyncComplete();
        }
        prevLastSync.current = res.data.last_sync;
      }
      isInitialized.current = true;
    } catch {
      setStatus({ connected: false });
      isInitialized.current = true;
    }
  }, [onSyncComplete]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const handleConnect = async () => {
    try {
      const res = await axios.get(`${API}/google-calendar/auth`);
      window.location.href = res.data.authorization_url;
    } catch {
      toast.error('Fehler beim Verbinden mit Google Calendar');
    }
  };

  const handleDisconnect = async () => {
    try {
      await axios.post(`${API}/google-calendar/disconnect`);
      setStatus({ connected: false });
      setSyncResult(null);
      toast.success('Google Calendar getrennt');
    } catch {
      toast.error('Fehler beim Trennen');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/google-calendar/sync`);
      setSyncResult(res.data);
      if (onSyncComplete) onSyncComplete(); // Sofort neu laden
      toast.success(`Sync: ${res.data.message}`);
    } catch {
      toast.error('Synchronisierung fehlgeschlagen');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4" data-testid="gcal-widget">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${status?.connected ? 'bg-green-500' : 'bg-gray-300'}`} />
          <div>
            <p className="text-sm font-semibold text-gray-900">Google Kalender</p>
            <p className="text-xs text-gray-500">
              {status === null ? 'Wird geladen...' : status?.connected
                ? 'Verbunden – Automatische Synchronisierung aktiv'
                : 'Nicht verbunden – Termine werden lokal gespeichert'}
            </p>
            {syncResult && (
              <p className="text-xs text-blue-600 mt-0.5">
                {syncResult.message}
                {syncResult.cancelled_from_google > 0 && (
                  <span className="text-orange-600 ml-1">– {syncResult.cancelled_from_google} Termin(e) storniert</span>
                )}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {status?.connected && canSync && (
            <button
              onClick={handleSync}
              disabled={syncing}
              data-testid="gcal-sync-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 disabled:opacity-50 transition-colors"
            >
              <svg className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {syncing ? 'Synchronisiere...' : 'Jetzt synchronisieren'}
            </button>
          )}
          {canManage && !status?.connected && status !== null && (
            <button
              onClick={handleConnect}
              data-testid="gcal-connect-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Google verbinden
            </button>
          )}
          {canManage && status?.connected && (
            <button
              onClick={handleDisconnect}
              data-testid="gcal-disconnect-btn"
              className="px-3 py-1.5 text-xs font-medium rounded-lg text-red-600 hover:bg-red-50 border border-red-200 transition-colors"
            >
              Trennen
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoogleCalendarWidget;
